import './coapplicantEditEmploymentPage.scss';

import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import { employedTypes } from '@thesavingsgroup/constants/employedTypes';
import { EmploymentType } from '@thesavingsgroup/enums/EmploymentType';
import { toCurrencyString } from '@thesavingsgroup/utils/toCurrencyString';
import { toNumericString } from '@thesavingsgroup/utils/toNumericString';
import isEmpty from 'lodash/isEmpty';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { Input } from '../../../components/Input/Input';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { NumberInput } from '../../../components/NumberInput/NumberInput';
import { Select } from '../../../components/Select/Select';
import { employedTypeOptions } from '../../../store/applicantStore/constants/employedTypeOptions';
import { employmentStatusOptions } from '../../../store/applicantStore/constants/employmentStatusOptions';
import { HALF_YEAR_IN_MONTHS_AS_STRING } from '../../../store/applicantStore/constants/halfYear';
import { monthsAtIncomeOptions } from '../../../store/applicantStore/constants/monthsAtIncomeOptions';
import { previousEmploymentTypeOptions } from '../../../store/applicantStore/constants/previousEmploymentTypeOptions';
import { unemployedTypeOptions } from '../../../store/applicantStore/constants/unemployedTypeOptions';
import { YES } from '../../../store/applicantStore/types/YesOrNo';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const {
    computed: {
      coapplicantEmployedGrossAnnualIncome,
      coapplicantUnemployedGrossAnnualIncome,
      coapplicantReviewEditPathname,
      coapplicantReviewEditProgress,
    },
    coapplicantCurrentIncomeEmployerName,
    coapplicantCurrentIncomeEmploymentType,
    coapplicantCurrentIncomeJobTitle,
    coapplicantCurrentIncomeMonthsAt,
    coapplicantPreviousIncomeEmployerName,
    coapplicantPreviousIncomeEmploymentType,
    coapplicantPreviousIncomeGrossAnnual,
    coapplicantPreviousIncomeJobTitle,
    isCoapplicantCurrentlyEmployed,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantCurrentIncomeEmployerName,
    setCoapplicantCurrentIncomeEmploymentType,
    setCoapplicantCurrentIncomeGrossAnnual,
    setCoapplicantCurrentIncomeJobTitle,
    setCoapplicantCurrentIncomeMonthsAt,
    setCoapplicantPreviousIncomeEmployerName,
    setCoapplicantPreviousIncomeEmploymentType,
    setCoapplicantPreviousIncomeGrossAnnual,
    setCoapplicantPreviousIncomeJobTitle,
    setIsCoapplicantCurrentlyEmployed,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm({
    criteriaMode: 'firstError',
    defaultValues: {
      currentEmployerNameValue: coapplicantCurrentIncomeEmployerName,
      currentIncomeMonthsAtValue: coapplicantCurrentIncomeMonthsAt,
      employmentTypeValue: coapplicantCurrentIncomeEmploymentType,
      grossAnnualIncomeValue: toCurrencyString(
        coapplicantEmployedGrossAnnualIncome,
      ),
      isCurrentlyEmployedValue: isCoapplicantCurrentlyEmployed,
      jobTitleValue: coapplicantCurrentIncomeJobTitle,
      previousEmployerNameValue: coapplicantPreviousIncomeEmployerName,
      previousEmploymentTypeValue: coapplicantPreviousIncomeEmploymentType,
      previousGrossAnnualIncomeValue: toCurrencyString(
        coapplicantPreviousIncomeGrossAnnual,
      ),
      previousJobTitleValue: coapplicantPreviousIncomeJobTitle,
      unemploymentGrossAnnualIncomeValue: toCurrencyString(
        coapplicantUnemployedGrossAnnualIncome,
      ),
      unemploymentTypeValue: coapplicantCurrentIncomeEmploymentType,
    },
    delayError: 200,
    mode: 'onTouched',
    shouldFocusError: false,
  });

  const shouldShowEmployedTypeFields =
    watch('isCurrentlyEmployedValue') === YES;

  const shouldShowPreviousEmployedTypeFields = employedTypes.includes(
    watch('previousEmploymentTypeValue') as EmploymentType,
  );

  const shouldShowPreviousEmploymentSection =
    watch('currentIncomeMonthsAtValue') === HALF_YEAR_IN_MONTHS_AS_STRING;

  const employmentStatusValidation = {
    required: t('validation.employmentStatus.required'),
  };

  const employmentTypeValidation = {
    required: t('validation.employmentType.required'),
  };

  const employerNameValidation = {
    required: t('validation.employerName.required'),
  };

  const jobTitleValidation = {
    required: t('validation.jobTitle.required'),
  };

  const monthsAtIncomeValidation = {
    required: t('validation.employmentDuration.required'),
  };

  const grossAnnualIncomeValidation = {
    setValueAs: (value: string): string => toNumericString(value),
    required: t('validation.grossAnnualIncome.required'),
    min: {
      value: 0,
      message: t('validation.grossAnnualIncome.min', { min: '$0' }),
    },
  };

  const finishEditing = (): void => {
    navigate(coapplicantReviewEditPathname);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      currentEmployerNameValue,
      currentIncomeMonthsAtValue,
      employmentTypeValue,
      grossAnnualIncomeValue,
      isCurrentlyEmployedValue,
      jobTitleValue,
      previousEmployerNameValue,
      previousEmploymentTypeValue,
      previousGrossAnnualIncomeValue,
      previousJobTitleValue,
      unemploymentGrossAnnualIncomeValue,
      unemploymentTypeValue,
    } = values;

    if (
      !isCurrentlyEmployedValue ||
      (shouldShowEmployedTypeFields && !employmentTypeValue) ||
      (!shouldShowEmployedTypeFields && !unemploymentTypeValue) ||
      (shouldShowEmployedTypeFields && !currentEmployerNameValue) ||
      (shouldShowEmployedTypeFields && !jobTitleValue) ||
      (shouldShowEmployedTypeFields && !grossAnnualIncomeValue) ||
      (!shouldShowEmployedTypeFields && !unemploymentGrossAnnualIncomeValue) ||
      !currentIncomeMonthsAtValue
    ) {
      return;
    }

    if (
      shouldShowPreviousEmploymentSection &&
      (!previousEmploymentTypeValue ||
        (shouldShowPreviousEmployedTypeFields && !previousEmployerNameValue) ||
        (shouldShowPreviousEmployedTypeFields && !previousJobTitleValue) ||
        !previousGrossAnnualIncomeValue)
    ) {
      return;
    }

    setCoapplicantCurrentIncomeMonthsAt(currentIncomeMonthsAtValue);
    setIsCoapplicantCurrentlyEmployed(isCurrentlyEmployedValue);

    if (shouldShowEmployedTypeFields) {
      setCoapplicantCurrentIncomeEmployerName(currentEmployerNameValue);
      setCoapplicantCurrentIncomeEmploymentType(employmentTypeValue);
      setCoapplicantCurrentIncomeGrossAnnual(grossAnnualIncomeValue);
      setCoapplicantCurrentIncomeJobTitle(jobTitleValue);
    } else {
      setCoapplicantCurrentIncomeEmployerName(null);
      setCoapplicantCurrentIncomeEmploymentType(unemploymentTypeValue);
      setCoapplicantCurrentIncomeGrossAnnual(
        unemploymentGrossAnnualIncomeValue,
      );
      setCoapplicantCurrentIncomeJobTitle(null);
    }

    if (shouldShowPreviousEmploymentSection) {
      setCoapplicantPreviousIncomeEmploymentType(previousEmploymentTypeValue);
      setCoapplicantPreviousIncomeGrossAnnual(previousGrossAnnualIncomeValue);
    } else {
      setCoapplicantPreviousIncomeEmploymentType(null);
      setCoapplicantPreviousIncomeGrossAnnual(null);
    }

    if (
      shouldShowPreviousEmploymentSection &&
      shouldShowPreviousEmployedTypeFields
    ) {
      setCoapplicantPreviousIncomeEmployerName(previousEmployerNameValue);
      setCoapplicantPreviousIncomeJobTitle(previousJobTitleValue);
    } else {
      setCoapplicantPreviousIncomeEmployerName(null);
      setCoapplicantPreviousIncomeJobTitle(null);
    }

    if (isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        finishEditing();
      }

      return;
    }

    finishEditing();
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="coapplicant-edit-employment-page" sx={theme.pageLayout}>
        <Header
          progress={coapplicantReviewEditProgress}
          showDrawerToggle
          onBackClick={finishEditing}
        />

        <main>
          <form
            data-testid={locator.pages.coapplicantEditEmployment.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.coapplicantEditEmployment.form.h1}>
                {t('CoapplicantEditEmploymentPage.title')}
              </h1>
              <h2 data-testid={locator.pages.coapplicantEditEmployment.form.h2}>
                {t('CoapplicantEditEmploymentPage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantEditEmployment.alert.error
                    }
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <Select
                    {...register(
                      'isCurrentlyEmployedValue',
                      employmentStatusValidation,
                    )}
                    error={errors.isCurrentlyEmployedValue}
                    errorTestId={
                      locator.pages.coapplicantEditEmployment.form
                        .employmentStatusSelectError
                    }
                    inputTestId={
                      locator.pages.coapplicantEditEmployment.form
                        .employmentStatusSelect
                    }
                    label={t('CoapplicantEditEmploymentPage.employmentStatus')}
                    options={employmentStatusOptions}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  {shouldShowEmployedTypeFields && (
                    <div>
                      <Select
                        {...register(
                          'employmentTypeValue',
                          employmentTypeValidation,
                        )}
                        error={errors.employmentTypeValue}
                        errorTestId={
                          locator.pages.coapplicantEditEmployment.form
                            .employmentTypeSelectError
                        }
                        inputTestId={
                          locator.pages.coapplicantEditEmployment.form
                            .employmentTypeSelect
                        }
                        label={t('CoapplicantEditEmploymentPage.type')}
                        options={employedTypeOptions}
                      />
                    </div>
                  )}

                  {!shouldShowEmployedTypeFields && (
                    <div>
                      <Select
                        {...register(
                          'unemploymentTypeValue',
                          employmentTypeValidation,
                        )}
                        error={errors.unemploymentTypeValue}
                        errorTestId={
                          locator.pages.coapplicantEditEmployment.form
                            .unemploymentTypeSelectError
                        }
                        inputTestId={
                          locator.pages.coapplicantEditEmployment.form
                            .unemploymentTypeSelect
                        }
                        label={t('CoapplicantEditEmploymentPage.type')}
                        options={unemployedTypeOptions}
                      />
                    </div>
                  )}
                </div>

                {shouldShowEmployedTypeFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <Input
                      {...register(
                        'currentEmployerNameValue',
                        employerNameValidation,
                      )}
                      data-testid={
                        locator.pages.coapplicantEditEmployment.form
                          .employerNameInput
                      }
                      error={errors.currentEmployerNameValue}
                      errorTestId={
                        locator.pages.coapplicantEditEmployment.form
                          .employerNameInputError
                      }
                      label={t('CoapplicantEditEmploymentPage.employerName')}
                    />
                  </div>
                )}

                {shouldShowEmployedTypeFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <Input
                      {...register('jobTitleValue', jobTitleValidation)}
                      data-testid={
                        locator.pages.coapplicantEditEmployment.form
                          .jobTitleInput
                      }
                      error={errors.jobTitleValue}
                      errorTestId={
                        locator.pages.coapplicantEditEmployment.form
                          .jobTitleInputError
                      }
                      label={t('CoapplicantEditEmploymentPage.jobTitle')}
                    />
                  </div>
                )}

                <div style={{ marginTop: '1.5rem' }}>
                  {shouldShowEmployedTypeFields && (
                    <div>
                      <NumberInput
                        {...register(
                          'grossAnnualIncomeValue',
                          grossAnnualIncomeValidation,
                        )}
                        data-testid={
                          locator.pages.coapplicantEditEmployment.form
                            .grossAnnualIncomeInput
                        }
                        error={errors.grossAnnualIncomeValue}
                        errorTestId={
                          locator.pages.coapplicantEditEmployment.form
                            .grossAnnualIncomeInputError
                        }
                        label={t(
                          'CoapplicantEditEmploymentPage.grossAnnualIncome',
                        )}
                        currency
                      />
                    </div>
                  )}

                  {!shouldShowEmployedTypeFields && (
                    <div>
                      <NumberInput
                        {...register(
                          'unemploymentGrossAnnualIncomeValue',
                          grossAnnualIncomeValidation,
                        )}
                        data-testid={
                          locator.pages.coapplicantEditEmployment.form
                            .unemploymentGrossAnnualIncomeInput
                        }
                        error={errors.unemploymentGrossAnnualIncomeValue}
                        errorTestId={
                          locator.pages.coapplicantEditEmployment.form
                            .unemploymentGrossAnnualIncomeInputError
                        }
                        label={t(
                          'CoapplicantEditEmploymentPage.grossAnnualIncome',
                        )}
                        currency
                      />
                    </div>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <Select
                    {...register(
                      'currentIncomeMonthsAtValue',
                      monthsAtIncomeValidation,
                    )}
                    error={errors.currentIncomeMonthsAtValue}
                    errorTestId={
                      locator.pages.coapplicantEditEmployment.form
                        .employmentDurationSelectError
                    }
                    inputTestId={
                      locator.pages.coapplicantEditEmployment.form
                        .employmentDurationSelect
                    }
                    label={t('CoapplicantEditEmploymentPage.duration')}
                    options={monthsAtIncomeOptions}
                  />
                </div>
              </div>

              {shouldShowPreviousEmploymentSection && (
                <div
                  data-testid={
                    locator.pages.coapplicantEditEmployment.form
                      .previousEmploymentRoot
                  }
                  style={{ marginBottom: '3rem' }}
                >
                  <h3
                    data-testid={
                      locator.pages.coapplicantEditEmployment.form
                        .previousEmploymentH3
                    }
                  >
                    {t('CoapplicantEditEmploymentPage.previousEmployment')}
                  </h3>

                  <div>
                    <Select
                      {...register(
                        'previousEmploymentTypeValue',
                        employmentTypeValidation,
                      )}
                      error={errors.previousEmploymentTypeValue}
                      errorTestId={
                        locator.pages.coapplicantEditEmployment.form
                          .previousEmploymentTypeSelectError
                      }
                      inputTestId={
                        locator.pages.coapplicantEditEmployment.form
                          .previousEmploymentTypeSelect
                      }
                      label={t('CoapplicantEditEmploymentPage.type')}
                      options={previousEmploymentTypeOptions}
                    />
                  </div>

                  {shouldShowPreviousEmployedTypeFields && (
                    <div style={{ marginTop: '1.5rem' }}>
                      <Input
                        {...register(
                          'previousEmployerNameValue',
                          employerNameValidation,
                        )}
                        data-testid={
                          locator.pages.coapplicantEditEmployment.form
                            .previousEmployerNameInput
                        }
                        error={errors.previousEmployerNameValue}
                        errorTestId={
                          locator.pages.coapplicantEditEmployment.form
                            .previousEmployerNameInputError
                        }
                        label={t('CoapplicantEditEmploymentPage.employerName')}
                      />
                    </div>
                  )}

                  {shouldShowPreviousEmployedTypeFields && (
                    <div style={{ marginTop: '1.5rem' }}>
                      <Input
                        {...register(
                          'previousJobTitleValue',
                          jobTitleValidation,
                        )}
                        data-testid={
                          locator.pages.coapplicantEditEmployment.form
                            .previousEmploymentJobTitleInput
                        }
                        error={errors.previousJobTitleValue}
                        errorTestId={
                          locator.pages.coapplicantEditEmployment.form
                            .previousEmploymentJobTitleInputError
                        }
                        label={t('CoapplicantEditEmploymentPage.jobTitle')}
                      />
                    </div>
                  )}

                  <div style={{ marginTop: '1.5rem' }}>
                    <NumberInput
                      {...register(
                        'previousGrossAnnualIncomeValue',
                        grossAnnualIncomeValidation,
                      )}
                      data-testid={
                        locator.pages.coapplicantEditEmployment.form
                          .previousEmploymentGrossAnnualIncomeInput
                      }
                      error={errors.previousGrossAnnualIncomeValue}
                      errorTestId={
                        locator.pages.coapplicantEditEmployment.form
                          .previousEmploymentGrossAnnualIncomeInputError
                      }
                      label={t(
                        'CoapplicantEditEmploymentPage.grossAnnualIncome',
                      )}
                      currency
                    />
                  </div>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div className="inline-buttons">
                  <Button
                    data-testid={
                      locator.pages.coapplicantEditEmployment.form.cancelButton
                    }
                    variant="secondary"
                    fullWidth
                    onClick={finishEditing}
                  >
                    {t('CoapplicantEditEmploymentPage.cancel')}
                  </Button>

                  <Button
                    data-testid={
                      locator.pages.coapplicantEditEmployment.form.submitButton
                    }
                    disabled={!isValid}
                    loader={isSyncingCoapplicant}
                    fullWidth
                    submit
                  >
                    {t('CoapplicantEditEmploymentPage.save')}
                  </Button>
                </div>
              </div>
            </div>
            <footer
              data-testid={locator.pages.coapplicantEditEmployment.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantEditEmploymentPage';
